import React, { FC, Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApolloCache, useMutation, useQuery } from '@apollo/client';
import { Box, Button, Typography } from '@mui/material';
import Loader from 'components/shared/loader/loader.component';
import InfoText from 'components/shared/info-text.component';
import PageTitle from 'components/shared/page-title/page-title.component';
import DeclineRequestDialog from 'components/shared/decline-request-dialog/decline-request-dialog.component';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import SupplierRequestTable from 'components/supplier-requests/supplier-request-table.component';
import { currentUserVar } from 'apollo/reactive-variables/user';
import {
    approveClinicRequestTemplate,
    declineClinicRequestTemplate,
    incomingClinicRequestsTemplate,
    outgoingClinicRequestsTemplate,
} from 'apollo/templates/clinic-request';
import { ClinicRequest, ClinicRequestStatus } from 'models/clinic-request';
import { permissions } from 'utils/permission';

/** Pending requests first, then the history; each group newest first (the server's order). */
const pendingFirst = (requests: ClinicRequest[]) => [
    ...requests.filter((request) => request.status === ClinicRequestStatus.PENDING),
    ...requests.filter((request) => request.status !== ClinicRequestStatus.PENDING),
];

/**
 * DD-3702: requests between this lab or clinic and the other side. Incoming ones are
 * answered here — by a technician admin on a lab, by a dentist admin or clinic manager on a
 * clinic; approving connects the two. Outgoing ones show their status.
 */
const ClinicRequests: FC = () => {
    const { t: translate } = useTranslation(['client-directory', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);
    const user = currentUserVar();
    const isLab = permissions.isLabTechnician(user?.type ?? '');
    const mayAnswer = isLab
        ? permissions.isTechnicianAdmin(user?.role)
        : permissions.isDentistAdmin(user?.role) || permissions.isClinicAdmin(user?.role);
    // On a lab, the other party is a clinic; on a clinic, a lab.
    const otherParty = (request: ClinicRequest) => (isLab ? request.clinic : request.lab);
    const partyLabelKey = isLab ? 'client-directory:CLINIC_REQUEST_CLINIC' : 'client-directory:SUPPLIER_REQUEST_LAB';
    const [toDecline, setToDecline] = useState<ClinicRequest>();

    const { data: incoming, loading: isIncomingLoading } = useQuery(incomingClinicRequestsTemplate, {
        fetchPolicy: 'cache-and-network',
        onError: (error) => showBoundary(error),
    });
    const { data: outgoing, loading: isOutgoingLoading } = useQuery(outgoingClinicRequestsTemplate, {
        fetchPolicy: 'cache-and-network',
        onError: (error) => showBoundary(error),
    });
    // Refetch by name: the list, the pending count on the tab and the directories.
    // The directories' states come from the server. The incoming requests are read twice —
    // the list and the tab's pending badge (same operation name, other variables) — so the
    // field is evicted and every reader refetches, rather than refetched by name.
    const refetchQueries = ['labDirectory', 'customerDirectory'];
    const evictIncoming = (cache: ApolloCache<unknown>) => {
        cache.evict({ id: 'ROOT_QUERY', fieldName: 'incomingClinicRequests' });
        cache.gc();
    };
    const [approve, { loading: isApproving }] = useMutation(approveClinicRequestTemplate, {
        refetchQueries,
        update: evictIncoming,
        onCompleted: ({ approveClinicRequest }) =>
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('client-directory:CLINIC_REQUEST_APPROVED_TOAST', {
                    name: otherParty(approveClinicRequest).name,
                }),
            }),
        onError: (error) => showBoundary(error),
    });
    const [decline, { loading: isDeclining }] = useMutation(declineClinicRequestTemplate, {
        refetchQueries,
        update: evictIncoming,
        onCompleted: () => setToDecline(undefined),
        onError: (error) => showBoundary(error),
    });

    if ((isIncomingLoading && !incoming) || (isOutgoingLoading && !outgoing)) return <Loader />;
    const incomingRequests: ClinicRequest[] = incoming?.incomingClinicRequests ?? [];
    const outgoingRequests: ClinicRequest[] = outgoing?.outgoingClinicRequests ?? [];

    const renderActions = (request: ClinicRequest) => (
        <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button
                onClick={() => setToDecline(request)}
                disabled={isApproving || isDeclining}
                data-test={`clinic-requests.decline-${request._id}`}
            >
                {translate('client-directory:DECLINE_SUPPLIER_REQUEST')}
            </Button>
            <Button
                variant="contained"
                onClick={() => approve({ variables: { requestId: request._id } })}
                disabled={isApproving || isDeclining}
                data-test={`clinic-requests.approve-${request._id}`}
            >
                {translate('client-directory:APPROVE_SUPPLIER_REQUEST')}
            </Button>
        </Box>
    );

    return (
        <Fragment>
            <PageTitle
                headingStyle="h3"
                title={translate(
                    isLab ? 'client-directory:CLINIC_REQUESTS_TO_LAB' : 'client-directory:LAB_REQUESTS_TO_CLINIC',
                )}
            />
            {/* The help texts explain the table; with no requests, "No requests yet" says it all. */}
            {incomingRequests.length ? (
                <InfoText
                    text={translate(
                        isLab
                            ? 'client-directory:CLINIC_REQUESTS_TO_LAB_INFO'
                            : 'client-directory:LAB_REQUESTS_TO_CLINIC_INFO',
                    )}
                />
            ) : undefined}
            {incomingRequests.length && !mayAnswer ? (
                <InfoText text={translate('client-directory:CLINIC_REQUEST_ADMINS_ONLY')} />
            ) : undefined}
            <Box mt={2} mb={4}>
                {incomingRequests.length ? (
                    <SupplierRequestTable
                        requests={pendingFirst(incomingRequests)}
                        labOf={otherParty}
                        partyLabelKey={partyLabelKey}
                        renderActions={mayAnswer ? renderActions : undefined}
                        dataTest="clinic-requests.incoming"
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        {translate('client-directory:NO_SUPPLIER_REQUESTS')}
                    </Typography>
                )}
            </Box>
            <PageTitle headingStyle="h3" title={translate('client-directory:CLINIC_REQUESTS_SENT')} />
            <Box mt={2}>
                {outgoingRequests.length ? (
                    <SupplierRequestTable
                        requests={pendingFirst(outgoingRequests)}
                        labOf={otherParty}
                        partyLabelKey={partyLabelKey}
                        dataTest="clinic-requests.outgoing"
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        {translate('client-directory:NO_SUPPLIER_REQUESTS')}
                    </Typography>
                )}
            </Box>
            <DeclineRequestDialog
                open={Boolean(toDecline)}
                partyName={toDecline ? otherParty(toDecline).name : ''}
                isDeclining={isDeclining}
                onClose={() => setToDecline(undefined)}
                onConfirm={(reason) => toDecline && decline({ variables: { requestId: toDecline._id, reason } })}
            />
        </Fragment>
    );
};

export default ClinicRequests;
